import React, { useState, useEffect } from 'react';
import { withRouter } from 'components/hoc';
import qs from 'qs';
import moment, { MomentInput } from 'moment';
import { Row } from 'reactstrap';
import { resolveGenderOptions } from '../../lib/gender';
import { validateDateOfBirth, validateEmail, validatePhoneNumber, validateZipCode } from '../../lib/validation';
import ActiveErrors from '../../components/common/activeErrors';
import { SectionHeading } from '../../components/common/standardComponents';
import * as routes from '../../routes';
import { setActivePatient, createPatient, searchAddress, getAddress } from '../../actions/rd2RefactorActions';
import { loadPatientSavePageData } from '../../actions/patientActions';
import { requestPacDemographics } from '../../actions/patientAccessCuratorActions';
import PatientEditForm from '../../components/patient/patientEditForm';
import { useAppDispatch, useAppSelector } from "../../store";
import { useAppHistory } from 'hooks';

interface PatientDetails {
	memberId: string;
	groupNumber: string;
	firstName: string;
	middleName: string;
	lastName: string;
	dateOfBirth: string;
	guardianName: string;
	homePhone: string;
	mobilePhone: string;
	alternatePhone: string;
	addressLine1: string;
	addressLine2: string;
	cityName: string;
	stateId: number;
	zipCode: string;
	email: string;
	notes: string;
	gender?: string;
	stateCode?: string;
	patientPoolUniqueToken?: string;
};

interface SelectedAddressOption {
	label: string;
	value: any;
}

interface PatientPool {
	displayName: string;
	prefix: string;
	integrationUniqueToken: string;
	uniqueToken: string;
	orgId: string | null;
	patientPoolId: number;
}

function PatientAddView(props: any) {

	const orderManagement: any = useAppSelector((state: any) => state.orderManagement);
	const patientConfig: any = useAppSelector((state: any) => state.config.patient);
	const idPgmStateDefault: any = useAppSelector((state: any) => state.config.instance.idPgmStateDefault);
	const routePrefix: any = useAppSelector((state: any) => state.config.instance.routePrefix);
	const lookupTables: any = useAppSelector((state: any) => state.lookupTables);
	const stateList: any = useAppSelector((state: any) => state.page.stateList);
	const patientPools: PatientPool[] = useAppSelector((state: any) => state.session.patientPools);
	const locationEntryPoint: any = useAppSelector((state: any) => state.session.locationEntryPoint);
	const isPatientPoolLocked = !!(locationEntryPoint?.isPatientPoolLocked && locationEntryPoint?.patientPoolUniqueToken);

	const initialPatientPoolUniqueToken = isPatientPoolLocked
		? locationEntryPoint.patientPoolUniqueToken
		: (patientConfig.enablePatientSearchWithPool && patientPools.length === 1 ? patientPools[0].uniqueToken : '');

	const initialPatientDetails: PatientDetails = {
		memberId: '',
		groupNumber: '',
		firstName: '',
		middleName: '',
		lastName: '',
		dateOfBirth: '',
		guardianName: '',
		homePhone: '',
		mobilePhone: '',
		alternatePhone: '',
		addressLine1: '',
		addressLine2: '',
		cityName: '',
		stateId: idPgmStateDefault ? idPgmStateDefault : 0,
		zipCode: '',
		email: '',
		notes: '',
        patientPoolUniqueToken: initialPatientPoolUniqueToken,
	};

	const [forceValidation, setForceValidation] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [isPacValidating, setIsPacValidating] = useState<boolean>(false);
	const [isPacValidated, setIsPacValidated] = useState<boolean>(false);
	const [phoneForceInvalid, setPhoneForceInvalid] = useState<boolean>(false);
	const [phoneForceInvalidMessage, setPhoneForceInvalidMessage] = useState<string>('');
	const [touchedInputs, setTouchedInputs] = useState <any>({});
	const [zipCodeForceInvalid, setZipCodeForceInvalid] = useState<boolean>(false);
	const [patientPoolForceInvalid, setPatientPoolForceInvalid] = useState<boolean>(false);
	const [isAddressLoading, setIsAddressLoading] = useState<boolean>(false);
	const [addressInputValue, setAddressInputValue] = useState<string | number>('');
	const [addressOptions, setAddressOptions] = useState<any>([]);
	const [pacDemographics, setPacDemographics] = useState<any>({});
	const [patientDetails, setPatientDetails] = useState<PatientDetails>(initialPatientDetails);
	const [selectedAddressOption, setSelectedAddressOption] = useState<SelectedAddressOption>({ label: 'Select...', value: {} });

	const dispatch = useAppDispatch();
	const history = useAppHistory();

	useEffect(() => {
		if (!stateList.length) dispatch(loadPatientSavePageData());

		const optionalParams = qs.parse(location.search, { ignoreQueryPrefix: true });
		let currentPatientDetails = { ...patientDetails };

		currentPatientDetails.memberId = (optionalParams.memberId ? optionalParams.memberId : '') as string;
		currentPatientDetails.firstName = (optionalParams.firstName ? optionalParams.firstName : '') as string;
		currentPatientDetails.lastName = (optionalParams.lastName ? optionalParams.lastName : '') as string;
		currentPatientDetails.dateOfBirth = (optionalParams.dateOfBirth
			? moment(optionalParams.dateOfBirth as MomentInput).format('MM/DD/YYYY')
			: '') as string;

		setPatientDetails(currentPatientDetails);
	}, []);

	const handleCancel = () => {
		if (history.navigate.length > 0) history.goBack();
		else dispatch(routes.patientSearchUrl(routePrefix));
	};

	const handlePatientChange = (e: any) => {
		var target = e.target;
		setPatientDetails((prevDetails: any) => ({
			...prevDetails,
			[target.name]: target.value,
		}));
		setTouchedInputs((prevTouched: any) => ({
			...prevTouched,
			[target.name]: true,
		}));

		if (target.name === 'homePhone' || target.name === 'mobilePhone') {
			updatePhoneForceInvalid();
		}
		if (target.name === 'zipCode') {
			setZipCodeForceInvalid(false);
		}

		if (patientConfig.enablePatientSearchWithPool && target.name === 'patientPoolUniqueToken') {
			if (target.value !== '') {
				const selectedPool = patientPools.find(pool => pool.uniqueToken === target.value);
				setPatientPoolForceInvalid(selectedPool ? false : true);
			}
			else {
				setPatientPoolForceInvalid(true);
			}
		}
		
	};

	const handleUpdatePatientInfoFromPac = (currentPatientDetailsFromPac: PatientDetails) => {
		setPatientDetails(currentPatientDetailsFromPac);
	};

	const handleValidate = (e: any) => {
		e.preventDefault();
        setForceValidation(true);
		let sanitizedPatientDetails = beforeSave();

		updatePhoneForceInvalid();

		if (validate()) {
            setIsPacValidating(true);
			dispatch(requestPacDemographics(sanitizedPatientDetails))
				.then((result: any) => {
					if (!result.error) {
                        setPacDemographics(result.payload.data.demographicResponse.patientDemographics);
					}
					else {
                        setPacDemographics({});
					}
					setIsPacValidating(false);
                    setIsPacValidated(true);
				});
		}
	};

	const handleSave = (e: any) => {
		e.preventDefault();

        setForceValidation(true);
		let sanitizedPatientDetails = beforeSave();

		updatePhoneForceInvalid();

		if (validate()) {
            setIsSaving(true);
			dispatch(createPatient(sanitizedPatientDetails))
				.then((result: any) => {
					if (result.error) {
                        setIsSaving(false);
					} else if (result.payload.data.errors && result.payload.data.errors.length > 0) {
						resolveSaveErrors(result.payload.data.errors);
					} else {
						return dispatch(setActivePatient(result.payload.data.patientDetails.referenceId));
					}
				})
				.then((result: any) => {
					if (result) {
						return dispatch(routes.patientDetails(routePrefix, result.payload.data.referenceId));
					}
				});
		}
	};

	const resolveSaveErrors = (errors: any) => {
		let currentZipCodeForceInvalid = false;
		if (errors && errors.length > 0) {
			currentZipCodeForceInvalid = errors.includes('zipcode');
		}
		setZipCodeForceInvalid(currentZipCodeForceInvalid);
        setIsSaving(false);
	};

	const beforeSave = () => {
		let currentPatientDetails = { ...patientDetails };
		const state = currentPatientDetails.stateId != null ? stateList.find((x: any) => x.id === Number(currentPatientDetails.stateId)) : null;
		currentPatientDetails.memberId = currentPatientDetails.memberId != '' ? currentPatientDetails.memberId.trim() : '';
		currentPatientDetails.groupNumber = currentPatientDetails.groupNumber != '' ? currentPatientDetails.groupNumber.trim() : '';
		currentPatientDetails.firstName = currentPatientDetails.firstName != '' ? currentPatientDetails.firstName.trim() : '';
		currentPatientDetails.middleName = currentPatientDetails.middleName != '' ? currentPatientDetails.middleName.trim() : '';
		currentPatientDetails.lastName = currentPatientDetails.lastName != '' ? currentPatientDetails.lastName.trim() : '';
		currentPatientDetails.dateOfBirth =
			currentPatientDetails.dateOfBirth != ''
				? moment.utc(currentPatientDetails.dateOfBirth.trim()).format('MM/DD/YYYY').toString()
				: '';
		currentPatientDetails.gender = currentPatientDetails.gender ? currentPatientDetails.gender.trim() : '';
		currentPatientDetails.homePhone = currentPatientDetails.homePhone != '' ? currentPatientDetails.homePhone.trim() : '';
		currentPatientDetails.mobilePhone = currentPatientDetails.mobilePhone != '' ? currentPatientDetails.mobilePhone.trim() : '';
		currentPatientDetails.alternatePhone = currentPatientDetails.alternatePhone != '' ? currentPatientDetails.alternatePhone.trim() : '';
		currentPatientDetails.cityName = currentPatientDetails.cityName != '' ? currentPatientDetails.cityName.trim() : '';
		currentPatientDetails.zipCode = currentPatientDetails.zipCode != '' ? currentPatientDetails.zipCode.trim() : '';
		currentPatientDetails.email = currentPatientDetails.email != '' ? currentPatientDetails.email.trim() : '';
		currentPatientDetails.notes = currentPatientDetails.notes != '' ? currentPatientDetails.notes.trim() : '';
		currentPatientDetails.stateCode = currentPatientDetails.stateId != null ? state.name : null;

		return currentPatientDetails;
	};

	const shouldValidate = (name: string) => {
		return forceValidation || touchedInputs[name] === true;
	};

	const updatePhoneForceInvalid = () => {
		let bothPhonesEmpty =
			!patientConfig.homePhone.isRequired &&
			!patientConfig.mobilePhone.isRequired &&
			!patientDetails.homePhone &&
			!patientDetails.mobilePhone;
        setPhoneForceInvalid(bothPhonesEmpty);
	};

	const validate = () => {
		let memberIdValid = patientConfig.memberId.isRequired ? patientDetails.memberId !== '' : true;
		let groupNumberValid = patientConfig.groupNumber.isRequired ? patientDetails.groupNumber !== '' : true;
		let firstNameValid = patientConfig.firstName.isRequired ? patientDetails.firstName !== '' : true;
		let middleNameValid = patientConfig.middleName.isRequired ? patientDetails.middleName !== '' : true;
		let lastNameValid = patientConfig.lastName.isRequired ? patientDetails.lastName !== '' : true;
		let dobValid = patientConfig.dateOfBirth.isRequired
			? validateDateOfBirth(patientDetails.dateOfBirth)
			: true;
		let genderValid = patientDetails.gender !== '';
		let addressValid = validateAddress();
		let phonesValid = validatePhones();
		let alternatePhoneValid = validateAlternatePhone();
		let emailValid = patientConfig.email.isRequired
			? validateEmail(patientDetails.email)
			: patientDetails.email && patientDetails.email.trim() !== ''
			? validateEmail(patientDetails.email)
			: true;
		let notesValid = patientConfig.notes.isRequired ? patientDetails.notes !== '' : true;
		let isPatientPoolValid = patientConfig.enablePatientSearchWithPool ? (patientDetails.patientPoolUniqueToken != '') : true;

		return (
			memberIdValid &&
			groupNumberValid &&
			firstNameValid &&
			middleNameValid &&
			lastNameValid &&
			dobValid &&
			genderValid &&
			addressValid &&
			phonesValid &&
			alternatePhoneValid &&
			emailValid &&
			notesValid &&
            isPatientPoolValid
		);
	};

	const validateAddress = () => {
		let address1Valid = patientConfig.addressLine1.isRequired ? patientDetails.addressLine1 !== '' : true;
		let address2Valid = patientConfig.addressLine2.isRequired
			? patientDetails.addressLine2 !== '' && patientDetails.addressLine2 !== null
			: true;
		let cityValid = patientConfig.city.isRequired ? patientDetails.cityName !== '' : true;
		let stateValid = patientConfig.state.isRequired ? patientDetails.stateId > 0 : true;
		let zipValid =
			patientConfig.zipCode.isRequired && !patientDetails.zipCode
				? false
				: !zipCodeForceInvalid && validateZipCode(patientDetails.zipCode);

		return address1Valid && address2Valid && cityValid && stateValid && zipValid;
	};

	const validatePhones = () => {
		let isValid = false;

		if (patientConfig.homePhone.isRequired) {
			isValid = validatePhoneNumber(patientDetails.homePhone);
		} else if (patientConfig.mobilePhone.isRequired) {
			isValid = validatePhoneNumber(patientDetails.mobilePhone);
		} else {
			let bothPhonesEmpty = !patientDetails.homePhone && !patientDetails.mobilePhone;
			let isHomePhoneValid =
				!patientDetails.homePhone || validatePhoneNumber(patientDetails.homePhone);
			let isMobilePhoneValid =
				!patientDetails.mobilePhone || validatePhoneNumber(patientDetails.mobilePhone);
			setPhoneForceInvalidMessage(bothPhonesEmpty ? 'Please provide a valid Home or Mobile Phone' : '');

			isValid = !bothPhonesEmpty && isHomePhoneValid && isMobilePhoneValid;
		}

		return isValid;
	};

	const validateAlternatePhone = () => {
		let isValid = true;

		if (patientConfig.alternatePhone.isRequired || !!patientDetails.alternatePhone) {
			isValid = validatePhoneNumber(patientDetails.alternatePhone);
		}

		return isValid;
	}

	const handleAddressChange = (possibleOption: SelectedAddressOption) => {
		if (possibleOption.value.address) {
			setIsAddressLoading(true);
            setSelectedAddressOption(possibleOption);
			const addressSearchCriteria = {
				query: possibleOption.value.address,
			};
			dispatch(getAddress(addressSearchCriteria))
				.then((response: any) => {
					const addressSearchResult = response.payload.data;
					const newPatientDetails = createPatientDetailsForAddress(addressSearchResult);
					setSelectedAddressOption(possibleOption);
					setIsAddressLoading(false);
                    setPatientDetails(newPatientDetails);
				});
		}
		else {
			setSelectedAddressOption({ label: 'Select...', value: {} });
		}
	}

	const createPatientDetailsForAddress = (addressSearchResult: any) => {
		const {
			addressLine1,
			addressLine2,
			city,
			state: stateCode,
			zipCode
		} = addressSearchResult;
		const state = stateList.find((x: any) => x.name === stateCode);
		const newPatientDetails = {
			...patientDetails,
			addressLine1: addressLine1,
			addressLine2: addressLine2,
			cityName: city,
			stateId: state ? state.id : 0,
			zipCode: zipCode.substring(0, 5),
		};

		return newPatientDetails;
	}

	const loadAddressOptions = async (inputValue: number, callback: any) => {
        setAddressInputValue(inputValue);
		if (inputValue < 5) {
			callback([]);
			return;
		}
		const result = await dispatch(searchAddress({ query: addressInputValue }));
		if (result.error) {
			console.error(result.error, addressInputValue);
			callback([]);
			return;
		}
		const addressSearchResults = result.payload.data;
		const addressOptions = addressSearchResults.map((a: any) => ({ label: a.address, value: a }));
		callback(addressOptions);
	}

	let genderOptions = resolveGenderOptions(
		patientConfig.allowUnknownGender,
		patientConfig.unknownGenderOptionText,
	);

	return (
		<div>
			<ActiveErrors />
			<SectionHeading label="Add Patient" />
			<Row className="g-0">
				<div className="col-lg-12">
					<PatientEditForm
						patientConfig={patientConfig}
						genderOptions={genderOptions}
						isSaving={isSaving}
						lookupTables={lookupTables}
						onCancel={handleCancel}
						onPatientChange={handlePatientChange}
						onSave={handleSave}
						patientDetails={patientDetails}
						phoneForceInvalid={phoneForceInvalid}
						phoneForceInvalidMessage={phoneForceInvalidMessage}
						shouldValidate={shouldValidate}
						stateList={stateList}
						zipCodeForceInvalid={zipCodeForceInvalid}
						handleAddressChange={handleAddressChange}
						loadAddressOptions={loadAddressOptions}
						isAddressLoading={isAddressLoading}
						selectedAddressOption={selectedAddressOption}
						isPacValidated={isPacValidated}
						isPacValidating={isPacValidating}
						handleValidate={handleValidate}
						pacDemographics={pacDemographics}
						handleUpdatePatientInfoFromPac={handleUpdatePatientInfoFromPac}
						patientPools={patientPools}
						patientPoolForceInvalid={patientPoolForceInvalid}
						disablePatientPoolSelection={isPatientPoolLocked}
					/>
				</div>
			</Row>
		</div>
	);
}

export default withRouter(PatientAddView);
